import i18n from '@/i18n'
import { useDictionaryStore } from '@/stores/dictionary'
import { dictItemLabel } from '@/utils/dict'

// 默认分页大小
export const DEFAULT_PAGE_SIZE = 50
export const MAX_PAGE_SIZE = 1000

export const DEFAULT_CURRENCY = 'CNY'

function currentLocale(): string {
  return (i18n.global.locale.value as string) || 'zh-CN'
}

function translate(key: string, fallback: string): string {
  const t = i18n.global.t
  const result = t(key)
  // 若 i18n 未命中（返回 key 本身），则使用 fallback
  return result === key ? fallback : result
}

/**
 * 通用 i18n 翻译（带 fallback），供组件使用。
 * 兼容旧中文值：若 code 包含中文，直接返回 code 本身。
 */
export function translateLabel(prefix: string, code: string | undefined, fallback?: string): string {
  if (!code) return ''
  if (/[\u4e00-\u9fa5]/.test(code)) return code
  return translate(`${prefix}.${code}`, fallback ?? code)
}

// ============================================
// 旧数据中文值 → 标准 code 映射（显示翻译与编辑提交规范化共用）
// ============================================
export const CARGO_TYPE_CN_MAP: Record<string, string> = {
  '普货': 'GENERAL',
  '危险品': 'DGR',
  '冷链': 'PERISHABLE',
  '锂电池': 'BATTERY',
}
export const TEMPERATURE_CN_MAP: Record<string, string> = {
  '常温': 'NORMAL',
}
export const FREIGHT_MODE_CN_MAP: Record<string, string> = {
  '空运直飞': 'DIRECT',
  '空运转陆运': 'AIR_TO_LAND',
  '联运': 'MULTIMODAL',
}
export const TRADE_TYPE_CN_MAP: Record<string, string> = {
  '一般贸易': 'GENERAL_TRADE',
  '快件': 'EXPRESS',
  '其他': 'OTHER',
}

export function toLocalISOString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * 将 ETD 值（Date 对象或 ISO 字符串）转换为本地日期参数（yyyy-MM-DD）。
 * 用于按 ETD 判断协议委托号 EQ 有效期的接口调用；无效/空值返回 undefined。
 */
export function toDateParam(value: string | Date | null | undefined): string | undefined {
  if (value === null || value === undefined || value === '') return undefined
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return undefined
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  const text = String(value).trim()
  if (!text) return undefined
  const date = new Date(text)
  if (!isNaN(date.getTime())) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return text.length >= 10 ? text.slice(0, 10) : undefined
}

/**
 * 主单号回显格式：11 位纯数字按“前 3 位-后 8 位”分隔（如 784-12345675）。
 * 非 11 位纯数字（历史/手工数据）原样返回；空值返回空串（便于调用方做 || 兜底）。
 */
export function formatMawbNo(no: string | null | undefined): string {
  if (!no) return ''
  return /^\d{11}$/.test(no) ? `${no.slice(0, 3)}-${no.slice(3)}` : no
}

export function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  return `${date.getMonth() + 1}/${date.getDate()}`
}

export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

export function formatCurrency(value: number | null | undefined, decimals: number = 2): string {
  if (value === null || value === undefined) return '0.00'
  const num = typeof value === 'string' ? parseFloat(value as unknown as string) : value
  if (isNaN(num)) return '0.00'
  return num.toLocaleString(currentLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

export function formatMoney(value: number | null | undefined, prefix: string = '', decimals: number = 0): string {
  if (value == null) return '-'
  const num = Number(value)
  if (isNaN(num)) return '-'
  return prefix + num.toLocaleString(currentLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

export function formatEtd(etd: string | null | undefined): string {
  if (!etd) return '-'
  const date = new Date(etd)
  if (isNaN(date.getTime())) return '-'
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
}

export function formatUldCount(count: number): string {
  if (!count) return '0'
  if (count === Math.floor(count)) return String(Math.floor(count))
  return count.toFixed(1)
}

export function formatNumber(num: number | undefined | null, options?: { decimals?: number; nullDisplay?: string; minimumFractionDigits?: number; maximumFractionDigits?: number }): string {
  const { decimals, nullDisplay = '-', minimumFractionDigits, maximumFractionDigits } = options || {}
  if (num === undefined || num === null) return nullDisplay
  const min = minimumFractionDigits ?? decimals ?? 0
  const max = maximumFractionDigits ?? decimals ?? 0
  return num.toLocaleString(currentLocale(), { minimumFractionDigits: min, maximumFractionDigits: max })
}

export { calcGrossProfit, profitClass } from './businessCalculations'

// ============================================
// Element Plus Tag 类型映射（非文本，不需 i18n）
// ============================================

export const BIZ_ORDER_STATUS_TAG_MAP: Record<string, string> = {
  DRAFT: 'info',
  PENDING: 'warning',
  PENDING_BOOKING: 'warning',
  PENDING_MATCH: 'warning',
  SUBMITTED: 'warning',
  MATCHED: 'success',
  TRACKING: '',
  LOCKED: 'danger',
  COMPLETED: 'success',
  CANCELLED: 'info'
}

export const PRODUCT_ORDER_STATUS_TAG_MAP: Record<string, string> = {
  DRAFT: 'info',
  PRE_BOOKED: 'info',
  PREBOOKED: 'info',
  MATCHED: 'warning',
  LOCKED: '',
  IN_TRANSIT: '',
  EXECUTING: '',
  ARRIVED: 'success',
  ALERT: 'danger',
  COMPLETED: 'success',
  ARCHIVED: 'success',
  DISSOLVED: 'info'
}

export const HAWB_STATUS_TAG_MAP: Record<string, string> = {
  DRAFT: 'info',
  CONFIRMED: '',
  ISSUED: 'success',
  VOID: 'danger'
}

// ============================================
// 状态文本：通过 i18n 翻译，未命中则回退到原 code
// ============================================

export function bizOrderStatusLabel(status: string | undefined): string {
  if (!status) return '-'
  return translate(`status.bizOrder.${status}`, status)
}

export function bizOrderStatusTagType(status: string | undefined): string {
  return BIZ_ORDER_STATUS_TAG_MAP[status || ''] || 'info'
}

export function productOrderStatusLabel(status: string | undefined): string {
  if (!status) return '-'
  return translate(`status.productOrder.${status}`, status)
}

export function productOrderStatusTagType(status: string | undefined): string {
  return PRODUCT_ORDER_STATUS_TAG_MAP[status || ''] || 'info'
}

export function hawbStatusLabel(status: string | undefined): string {
  if (!status) return '-'
  return translate(`status.hawb.${status}`, status)
}

export function hawbStatusTagType(status: string | undefined): string {
  return HAWB_STATUS_TAG_MAP[status || ''] || 'info'
}

// ============================================
// 货物类型 / ULD 板型：通过 i18n 翻译
// ============================================

export function cargoTypeName(code: string | undefined): string {
  if (!code) return ''
  const mapped = CARGO_TYPE_CN_MAP[code] || code
  const upper = mapped.toUpperCase()
  return translate(`type.cargoType.${upper}`, mapped)
}

export function temperatureName(code: string | undefined): string {
  if (!code) return ''
  const mapped = TEMPERATURE_CN_MAP[code] || code
  return translate(`type.temperature.${mapped}`, mapped)
}

export function stackDirectionName(code: string | undefined): string {
  return translateLabel('type.stackDirection', code)
}

export function warehouseDataStatusName(code: string | undefined): string {
  return translateLabel('type.warehouseDataStatus', code)
}

/**
 * 从字典缓存同步取项标签（按 locale 选 name/nameEn）。
 * 仅在 pinia 已初始化后可用；render 中调用会建立响应式依赖，
 * 字典异步加载完成后（store 整体替换 cache 引用）自动触发重渲染。
 * 未命中（缓存未加载/code 不存在）返回 undefined，由调用方 fallback。
 */
function dictCachedLabel(dictCode: string, code: string): string | undefined {
  try {
    const items = useDictionaryStore().cache.get(dictCode)
    const hit = items?.find(item => item.code === code)
    return hit ? dictItemLabel(hit) : undefined
  } catch {
    return undefined
  }
}

/** 业务委托的运输方式（与产品委托统一使用 transport_mode 字典体系） */
export function freightModeLabel(code: string | undefined): string {
  if (!code) return ''
  const mapped = FREIGHT_MODE_CN_MAP[code] || code
  return transportModeLabel(mapped)
}

/** 报关方式（存 tradeType 体系值） */
export function tradeTypeName(code: string | undefined): string {
  if (!code) return ''
  const mapped = TRADE_TYPE_CN_MAP[code] || code
  return translate(`type.tradeType.${mapped}`, mapped)
}

export function uldTypeLabel(code: string | undefined): string {
  if (!code) return ''
  const upper = code.toUpperCase()
  return translate(`type.uldType.${upper}`, code)
}

// ============================================
// 运输方式 / 地区：通过 i18n 翻译（兼容旧中文值）
// ============================================

export function transportModeLabel(code: string | undefined): string {
  if (!code) return ''
  // 兼容旧数据：中文值直接返回（字典/i18n key 不会命中）
  if (/[\u4e00-\u9fa5]/.test(code)) return code
  // 优先从 transport_mode 字典缓存取标签（客户在字典管理新增的选项即刻生效）
  const dictLabel = dictCachedLabel('transport_mode', code)
  if (dictLabel) return dictLabel
  return translate(`type.transportMode.${code}`, code)
}

export function regionLabel(code: string | undefined): string {
  if (!code) return ''
  if (/[\u4e00-\u9fa5]/.test(code)) return code
  return translate(`type.region.${code}`, code)
}
